import {
  DEFAULT_SUGGESTIONS,
  SUGGESTIONS,
  type SuggestionSet,
} from "@/data/knowledge/suggestions";
import type { Locale } from "@/lib/translations";
import { matchPath, sitePathsIn } from "./routes";

/**
 * Picks which questions to offer, from where the visitor is and what was just
 * said.
 *
 * No model call. Which questions to show is a lookup, and the same request that
 * produced the answer already told us everything needed: the page the visitor
 * is on, and the pages the answer named. Asking a model to invent follow-ups
 * would cost a third round trip and give a different set every time — the same
 * flakiness that made the navigation offer unreliable.
 */

function setForPath(pathname: string | null): SuggestionSet {
  const entry = pathname ? matchPath(pathname) : null;
  return (entry && SUGGESTIONS[entry.key]) || DEFAULT_SUGGESTIONS;
}

/**
 * Questions for an empty conversation, chosen by the page being read.
 *
 * A visitor on the mobile-apps page is asked about apps, not about workflow
 * automation. Pages with no topic of their own fall back to questions about the
 * company, which are answerable everywhere.
 */
export function openingSuggestions(
  pathname: string | null,
  locale: Locale,
): readonly string[] {
  return setForPath(pathname).opening.map((q) => q[locale]);
}

/**
 * Questions to continue with, chosen by what the answer was about.
 *
 * The topic comes from the page the answer named, which is the assistant's own
 * statement of what it just talked about, and falls back to the page being read
 * when the answer named none. Anything the visitor has already asked is
 * dropped, so the same chip is never offered twice.
 */
export function followUpSuggestions(
  lastAnswer: string,
  pathname: string | null,
  locale: Locale,
  alreadyAsked: readonly string[] = [],
): readonly string[] {
  const named = sitePathsIn(lastAnswer);
  const topical = named.length > 0 ? SUGGESTIONS[named[0].key] : undefined;
  const set = topical ?? setForPath(pathname);

  const asked = new Set(alreadyAsked.map((q) => q.trim().toLowerCase()));
  const unasked = set.followUp
    .map((q) => q[locale])
    .filter((q) => !asked.has(q.trim().toLowerCase()));

  // Every follow-up on this topic has been asked already: fall back to the
  // general ones rather than showing nothing, and drop those already asked too.
  if (unasked.length > 0) return unasked;
  return DEFAULT_SUGGESTIONS.followUp
    .map((q) => q[locale])
    .filter((q) => !asked.has(q.trim().toLowerCase()));
}
